import { ShieldCheck } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StateMessage } from '@/admin-area/ui/StateMessage';
import { Button } from '@/components/ui/button';
import { driveClientId, requestDriveToken, type ConsentResult } from '../drive-token';

/**
 * Visibility B (#788 §4): the console asks once for the metadata of the user's own Drive, so the
 * Panoramica shows only what the person already sees there. The token stays in memory.
 */
export function ConsentPanel({ onGranted }: { readonly onGranted: () => void }) {
  const { t } = useTranslation('archive');
  const [result, setResult] = useState<ConsentResult | 'idle' | 'asking'>('idle');

  if (driveClientId() === '') {
    return (
      <StateMessage
        icon={ShieldCheck}
        title={t('overview.consent.unconfigured')}
        text={t('overview.consent.unconfiguredText')}
      />
    );
  }

  const ask = async () => {
    setResult('asking');
    const answer = await requestDriveToken();
    setResult(answer);
    if (answer === 'granted') onGranted();
  };

  const refused = result === 'denied' || result === 'failed';
  return (
    <StateMessage
      icon={ShieldCheck}
      {...(refused ? { role: 'alert' as const } : {})}
      title={result === 'denied' ? t('overview.consent.denied') : t('overview.consent.title')}
      text={result === 'failed' ? t('overview.consent.failed') : t('overview.consent.text')}
      action={
        <Button disabled={result === 'asking'} onClick={() => void ask()}>
          {refused ? t('overview.consent.retry') : t('overview.consent.allow')}
        </Button>
      }
    />
  );
}
